import {
  Alert,
  AlertDialog,
  AlertDialogBody,
  AlertDialogCloseButton,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Divider,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Stack,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react'
import { useEffect, useRef, useState } from 'react'
import useHandleError from '../../../../hooks/useHandleError'
import { DeceasedReasonsSelect } from './DeceasedReasonsSelect'
import { deleteField, updateDoc } from 'firebase/firestore'
import useConfigList from '../../../../hooks/useConfigList'
import userDoc from '../../../../utils/userDoc'
import { primaryServices } from '../../../../utils/firebaseServiceContext'

export const AccountStatus = ({
  lockedDate,
  causeOfDeath,
  uid,
  archived,
}: {
  lockedDate?: string
  causeOfDeath?: string
  uid: string
  archived?: boolean
}) => {
  const {
    isOpen: isDeceasedModalOpen,
    onClose: onDeceasedModalClose,
    onOpen: onDeceasedModalOpen,
  } = useDisclosure()
  const {
    isOpen: isArchiveModalOpen,
    onClose: onArchiveModalClose,
    onOpen: onArchiveModalOpen,
  } = useDisclosure()
  const {
    isOpen: isUnarchiveModalOpen,
    onClose: onUnarchiveModalClose,
    onOpen: onUnarchiveModalOpen,
  } = useDisclosure()
  const deceasedCancelRef = useRef<any>()
  const archiveCancelRef = useRef<any>()
  const unarchiveCancelRef = useRef<any>()

  const [input, setInput] = useState('')
  const [selectedCauseOfDeath, setCauseOfDeath] = useState(causeOfDeath || '')
  const toast = useToast()
  const handleError = useHandleError()
  const [loading, setLoading] = useState(false)
  const [archiveLoading, setArchiveLoading] = useState(false)
  const deceasedReasons = useConfigList('deceasedReasons') as Record<
    string,
    string
  >

  // Initialize cause of death when the component mounts or causeOfDeath prop changes
  useEffect(() => {
    if (causeOfDeath) {
      setCauseOfDeath(causeOfDeath)
    }
  }, [causeOfDeath])

  const handleDeceasedChange = async (
    lockedDate?: string,
    causeOfDeath?: string
  ) => {
    const ref = userDoc(uid, primaryServices.firestore)

    if (lockedDate) {
      // When setting deceased status, update both lockedDate and profile with cause of death
      // Also ensure archived is removed if setting deceased
      return updateDoc(ref, {
        lockedDate,
        'profile.isDeceased': true,
        'profile.yearOfDeath': lockedDate.substring(0, 4),
        'profile.causeOfDeath': causeOfDeath,
        archived: deleteField(),
      })
    } else {
      // When removing deceased status, clear all related fields
      return updateDoc(ref, {
        lockedDate: deleteField(),
        'profile.isDeceased': false,
        'profile.yearOfDeath': deleteField(),
        'profile.causeOfDeath': deleteField(),
      })
    }
  }

  const handleArchiveChange = async (shouldArchive: boolean) => {
    const ref = userDoc(uid, primaryServices.firestore)

    if (shouldArchive) {
      // When archiving, ensure deceased status is removed
      return updateDoc(ref, {
        archived: true,
        lockedDate: deleteField(),
        'profile.isDeceased': false,
        'profile.yearOfDeath': deleteField(),
        'profile.causeOfDeath': deleteField(),
      })
    } else {
      // When unarchiving, just remove the archived field
      return updateDoc(ref, {
        archived: deleteField(),
      })
    }
  }

  return (
    <Stack spacing={6}>
      {/* Deceased Status Section */}
      <Stack spacing={3}>
        <Heading size='md'>Deceased?</Heading>
        {lockedDate ? (
          <>
            <Text>Yes: {lockedDate}</Text>
            {causeOfDeath && (
              <Text>
                Cause of Death: {deceasedReasons[causeOfDeath] || causeOfDeath}
              </Text>
            )}
            <Box>
              <Button
                variant='link'
                onClick={() => {
                  if (window.confirm('Remove deceased status?')) {
                    handleDeceasedChange(undefined)
                  }
                }}
              >
                Change
              </Button>
            </Box>
          </>
        ) : (
          <>
            <Text>No</Text>
            <Box>
              <Button
                variant='link'
                onClick={onDeceasedModalOpen}
                isDisabled={archived}
              >
                Change
              </Button>
            </Box>
          </>
        )}
      </Stack>

      <Divider />

      {/* Archive Status Section */}
      <Stack spacing={3}>
        <Heading size='md'>Archived?</Heading>
        {archived ? (
          <>
            <Text color='orange.500'>Yes - User cannot log in</Text>
            <Box>
              <Button
                variant='link'
                onClick={onUnarchiveModalOpen}
                isLoading={archiveLoading}
              >
                Unarchive
              </Button>
            </Box>
          </>
        ) : (
          <>
            <Text>No</Text>
            <Box>
              <Button
                variant='link'
                onClick={onArchiveModalOpen}
                isDisabled={!!lockedDate}
              >
                Archive
              </Button>
            </Box>
          </>
        )}
      </Stack>

      {/* Deceased Status Modal */}
      <AlertDialog
        isOpen={isDeceasedModalOpen}
        onClose={onDeceasedModalClose}
        leastDestructiveRef={deceasedCancelRef}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>Set Deceased Status</AlertDialogHeader>
            <AlertDialogCloseButton />
            <AlertDialogBody>
              <Stack spacing={4}>
                <Alert>This user will no longer receive reminder emails.</Alert>
                <FormControl>
                  <FormLabel>Date of Death</FormLabel>
                  <Input
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    type='date'
                  />
                </FormControl>
                <DeceasedReasonsSelect
                  value={selectedCauseOfDeath}
                  onChange={(value) => setCauseOfDeath(value)}
                />
              </Stack>
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button
                ref={deceasedCancelRef}
                onClick={onDeceasedModalClose}
                colorScheme='gray'
              >
                Cancel
              </Button>
              <Button
                ml={3}
                isDisabled={
                  loading ||
                  input.length === 0 ||
                  selectedCauseOfDeath.length === 0
                }
                isLoading={loading}
                onClick={async () => {
                  if (!input) {
                    return toast({
                      status: 'warning',
                      description: 'Please enter a date.',
                    })
                  }

                  if (!selectedCauseOfDeath) {
                    return toast({
                      status: 'warning',
                      description: 'Please select a cause of death.',
                    })
                  }

                  setLoading(true)

                  try {
                    await handleDeceasedChange(input, selectedCauseOfDeath)
                    onDeceasedModalClose()
                    toast({
                      status: 'success',
                      description: 'Set deceased status successfully.',
                    })
                    setInput('')
                    setCauseOfDeath('')
                  } catch (e) {
                    handleError(e)
                  } finally {
                    setLoading(false)
                  }
                }}
              >
                Submit
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Archive Modal */}
      <AlertDialog
        isOpen={isArchiveModalOpen}
        onClose={onArchiveModalClose}
        leastDestructiveRef={archiveCancelRef}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>Archive User</AlertDialogHeader>
            <AlertDialogCloseButton />
            <AlertDialogBody>
              <Stack spacing={4}>
                <Alert status='warning'>
                  <Stack spacing={2}>
                    <Text fontWeight='bold'>
                      This will prevent the user from logging in.
                    </Text>
                    <Text>
                      The user will see an error message directing them to
                      contact support.
                    </Text>
                    <Text>
                      If the user is currently logged in, they will be logged
                      out.
                    </Text>
                  </Stack>
                </Alert>
              </Stack>
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button
                ref={archiveCancelRef}
                onClick={onArchiveModalClose}
                colorScheme='gray'
              >
                Cancel
              </Button>
              <Button
                ml={3}
                colorScheme='orange'
                isLoading={archiveLoading}
                onClick={async () => {
                  setArchiveLoading(true)

                  try {
                    await handleArchiveChange(true)
                    onArchiveModalClose()
                    toast({
                      status: 'success',
                      description: 'User archived successfully.',
                    })
                  } catch (e) {
                    handleError(e)
                  } finally {
                    setArchiveLoading(false)
                  }
                }}
              >
                Archive User
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Unarchive Modal */}
      <AlertDialog
        isOpen={isUnarchiveModalOpen}
        onClose={onUnarchiveModalClose}
        leastDestructiveRef={unarchiveCancelRef}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>Unarchive User</AlertDialogHeader>
            <AlertDialogCloseButton />
            <AlertDialogBody>
              <Stack spacing={4}>
                <Alert status='info'>
                  <Stack spacing={2}>
                    <Text fontWeight='bold'>
                      This will allow the user to log in again.
                    </Text>
                    <Text>
                      The user will regain access to their account and be able
                      to use the application normally.
                    </Text>
                  </Stack>
                </Alert>
              </Stack>
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button
                ref={unarchiveCancelRef}
                onClick={onUnarchiveModalClose}
                colorScheme='gray'
              >
                Cancel
              </Button>
              <Button
                ml={3}
                colorScheme='green'
                isLoading={archiveLoading}
                onClick={async () => {
                  setArchiveLoading(true)

                  try {
                    await handleArchiveChange(false)
                    onUnarchiveModalClose()
                    toast({
                      status: 'success',
                      description: 'User unarchived successfully.',
                    })
                  } catch (e) {
                    handleError(e)
                  } finally {
                    setArchiveLoading(false)
                  }
                }}
              >
                Unarchive User
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Stack>
  )
}
